import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * slideshow — Shrine "Slideshow" at CONFIG-BY-CONFIG parity. The home CENTERPIECE: a hero CAROUSEL of
 * slides. Each SLIDE is an inline child block (LEGACY model): a background image (desktop + optional mobile
 * image) under a tunable scrim, an overlay heading (inline-richtext bold-word HIGHLIGHT) + subheading + a
 * primary button + an ATC button, a 9-position text-box alignment, per-slide text alignment (desktop +
 * mobile), and a full per-slide COLOR SCHEME (one of five presets or a fully custom palette: background,
 * gradient, text, solid-button bg/text, outline-button colours) plus a white/black transparent-container
 * tint.
 *
 * Section-level: device VISIBILITY, full-bleed/grid LAYOUT, image RATIO, pagination VISUAL
 * (dots/counter/numbers) with desktop + mobile POSITION, autoplay (+ speed), a "show content below images
 * on mobile" toggle, and an accessibility label — plus the shared "Section" universal group (colorScheme /
 * padding). The universal `width` control is intentionally NOT offered: width is governed by `layout`.
 *
 * DEFERRED (Shopify-platform only, one-line reasons in the Component): `atcProduct` (a Shopify product
 * binding — "if empty adds the main product-page product"; the storefront slide carries no product context
 * yet) and `atcSkipCart` (governs that same ATC's add-then-redirect behaviour, meaningless without the
 * product binding + a wired cart). The two `__header_*` rows are Shopify inspector HEADERS (no value, no
 * field type) — represented here as inspector GROUP labels instead.
 */
export const slideshowSchema = {
    type: 'slideshow',
    name: 'Slideshow',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'Layout',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        {
            id: 'layout',
            type: 'select',
            label: 'Layout',
            group: 'Layout',
            default: 'full_bleed',
            options: [
                { value: 'full_bleed', label: 'Full bleed (edge to edge)' },
                { value: 'grid', label: 'Page width' },
            ],
        },
        // 2026-08-16: replaced Shrine's `slideHeight` (fixed hero heights that CROPPED the image) with the
        // image-with-text `imageRatio` scale — founder ask: the slideshow must offer the SAME ratio control.
        // Legacy instances that saved a `slideHeight` keep their fixed-height rendering until this is touched.
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Layout',
            default: 'adapt',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'square', label: 'Square (1:1)' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'landscape', label: 'Landscape (3:2)' },
            ],
        },
        // ── Slider / pagination ──────────────────────────────────────────────────
        {
            id: 'sliderVisual',
            type: 'select',
            label: 'Pagination style',
            group: 'Slider',
            default: 'dots',
            options: [
                { value: 'dots', label: 'Dots' },
                { value: 'counter', label: 'Counter (1 / N)' },
                { value: 'numbers', label: 'Numbers' },
            ],
        },
        {
            id: 'desktopPaginationPosition',
            type: 'select',
            label: 'Pagination position (desktop)',
            group: 'Slider',
            default: 'over',
            options: [
                { value: 'under', label: 'Under the section' },
                { value: 'over', label: 'Over the section' },
            ],
        },
        {
            id: 'autoRotate',
            type: 'checkbox',
            label: 'Auto-rotate slides',
            group: 'Slider',
            default: false,
            info: 'Autoplay needs JS — the arrows, dots and swipe always work.',
        },
        {
            id: 'changeSlidesSpeed',
            type: 'range',
            label: 'Change slides every',
            group: 'Slider',
            min: 3,
            max: 9,
            step: 2,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'autoRotate', equals: true },
        },
        // ── Mobile (Shrine `__header_mobile`). ───────────────────────────────────
        {
            id: 'showTextBelow',
            type: 'checkbox',
            label: 'Show content below images on mobile',
            group: 'Mobile',
            default: true,
        },
        {
            id: 'mobilePaginationPosition',
            type: 'select',
            label: 'Pagination position (mobile)',
            group: 'Mobile',
            default: 'under',
            info: 'Over only applies if “Show content below images on mobile” is disabled.',
            options: [
                { value: 'under', label: 'Under the section' },
                { value: 'over', label: 'Over the section' },
            ],
        },
        // ── Accessibility (Shrine `__header_accessibility`). ─────────────────────
        {
            id: 'accessibilityInfo',
            type: 'text',
            label: 'Slideshow description',
            group: 'Accessibility',
            default: 'Diaporama de notre marque',
            defaultKey: 'a11y.brand_slideshow',
            info: 'Announced by screen readers to describe the slideshow.',
            maxLength: 120,
        },
        // ── Section (universal) ──────────────────────────────────────────────────
        // The universal `width` control is dropped: section width here is governed entirely by the `layout`
        // field (full_bleed edge-to-edge vs grid page-width), which the Component reads via `pageWidth`. The
        // slideshow never consumes `contentWidthStyle`/`s.width`, so offering Width too would be a dead, and
        // conflicting, control. Spread the rest of the universal group (colour scheme + padding) unchanged.
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields.filter((f) => f.id !== 'width'),
    ],
    // The SLIDE inline block (Shrine `slide`): media + overlay heading/subtext/buttons + per-slide scheme.
    blocks: [
        {
            type: 'slide',
            name: 'Slide',
            max: 8,
            fields: [
                // ── Images (Shrine `__header_images`). ───────────────────────────────
                { id: 'image', type: 'image', label: 'Image', group: 'Images' },
                // An uploaded clip REPLACES the still on the same slide (founder ask 2026-09-16: "a slide must be
                // able to hold a video, not only images"). It plays exactly like an image — silent, looping,
                // playsInline, NO player chrome — the same contract as image-with-text and the hero slideshows:
                // a slide has no playback controls because an image has none either. The still stays useful as the
                // POSTER, so the slide is never an empty band while the clip is still deferred. Upload quality
                // (Standard / Detail / Original) is chosen on the drop zone by the shared video control.
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    group: 'Images',
                    info: 'Upload an MP4/WebM. When set, it replaces the image on this slide and plays inline like one — silent, looping, no play bar.',
                },
                {
                    id: 'mobileImage',
                    type: 'image',
                    label: 'Mobile image',
                    group: 'Images',
                    info: 'If empty, the main image is shown on mobile too.',
                },
                {
                    id: 'mobileVideo',
                    type: 'video',
                    label: 'Mobile video',
                    group: 'Images',
                    info: 'If empty, the main video (or the mobile image) is shown on mobile too.',
                },
                playButtonField({
                    defaultOn: false,
                    group: 'Images',
                    visibleWhen: { field: 'video', empty: false },
                }),
                {
                    id: 'imageOverlayOpacity',
                    type: 'range',
                    label: 'Image overlay opacity',
                    group: 'Images',
                    min: 0,
                    max: 100,
                    step: 10,
                    unit: '%',
                    default: 0,
                },
                // ── General (Shrine `__header_general`). ─────────────────────────────
                {
                    id: 'heading',
                    type: 'richtext',
                    label: 'Heading',
                    group: 'General',
                    default: { format: 'html', html: '<p>Titre de la diapositive</p>' },
                    info: 'Bold a word to apply the highlight color.',
                },
                {
                    id: 'titleHighlightColor',
                    type: 'color',
                    label: 'Highlight color',
                    group: 'General',
                    default: { ref: 'theme', token: 'accent' },
                },
                {
                    id: 'headingSize',
                    type: 'select',
                    label: 'Heading size',
                    group: 'General',
                    default: 'h1',
                    options: [
                        { value: 'h2', label: 'Small' },
                        { value: 'h1', label: 'Medium' },
                        { value: 'h0', label: 'Large' },
                    ],
                },
                {
                    id: 'subheading',
                    type: 'text',
                    label: 'Subheading',
                    group: 'General',
                    default: 'Racontez votre histoire en images.',
                    defaultKey: 'section.slideshow_subtitle',
                    maxLength: 240,
                },
                {
                    id: 'buttonLabel',
                    type: 'text',
                    label: 'Button label',
                    group: 'General',
                    default: 'Découvrir',
                    defaultKey: 'hero.shop_now',
                    maxLength: 40,
                },
                { id: 'link', type: 'link', label: 'Button link', group: 'General' },
                {
                    id: 'buttonStyleSecondary',
                    type: 'checkbox',
                    label: 'Use outline button style',
                    group: 'General',
                    default: false,
                },
                {
                    id: 'atcButtonLabel',
                    type: 'text',
                    label: 'Add to cart button label',
                    group: 'General',
                    info: 'Leave blank to hide the add-to-cart button.',
                    maxLength: 40,
                },
                // ── Box / alignment ──────────────────────────────────────────────────
                {
                    id: 'boxAlign',
                    type: 'select',
                    label: 'Box alignment',
                    group: 'General',
                    default: 'middle-center',
                    options: [
                        { value: 'top-left', label: 'Top left' },
                        { value: 'top-center', label: 'Top center' },
                        { value: 'top-right', label: 'Top right' },
                        { value: 'middle-left', label: 'Middle left' },
                        { value: 'middle-center', label: 'Middle center' },
                        { value: 'middle-right', label: 'Middle right' },
                        { value: 'bottom-left', label: 'Bottom left' },
                        { value: 'bottom-center', label: 'Bottom center' },
                        { value: 'bottom-right', label: 'Bottom right' },
                    ],
                },
                {
                    id: 'showTextBox',
                    type: 'checkbox',
                    label: 'Show text box',
                    group: 'General',
                    default: true,
                    info: 'When off, the text sits directly over the image (transparent container).',
                },
                {
                    id: 'textAlignment',
                    type: 'select',
                    label: 'Text alignment',
                    group: 'General',
                    default: 'center',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
                // ── Color scheme ─────────────────────────────────────────────────────
                {
                    id: 'colorScheme',
                    type: 'select',
                    label: 'Color scheme',
                    group: 'General',
                    default: 'background-1',
                    options: [
                        { value: 'accent-1', label: 'Accent 1' },
                        { value: 'accent-2', label: 'Accent 2' },
                        { value: 'background-1', label: 'Background 1' },
                        { value: 'background-2', label: 'Background 2' },
                        { value: 'inverse', label: 'Inverse' },
                        { value: 'custom', label: 'Custom' },
                    ],
                },
                {
                    id: 'customColorsBackground',
                    type: 'color',
                    label: 'Custom background',
                    group: 'General',
                    default: { ref: 'custom', value: '#FFFFFF' },
                    visibleWhen: { field: 'colorScheme', equals: 'custom' },
                },
                {
                    id: 'customGradientBackground',
                    type: 'gradient',
                    label: 'Custom gradient background',
                    group: 'General',
                    visibleWhen: { field: 'colorScheme', equals: 'custom' },
                },
                {
                    id: 'customColorsText',
                    type: 'color',
                    label: 'Custom text',
                    group: 'General',
                    default: { ref: 'custom', value: '#2E2A39' },
                    visibleWhen: { field: 'colorScheme', equals: 'custom' },
                },
                {
                    id: 'customColorsSolidButtonBackground',
                    type: 'color',
                    label: 'Custom solid button background',
                    group: 'General',
                    default: { ref: 'custom', value: '#dd1d1d' },
                    visibleWhen: { field: 'colorScheme', equals: 'custom' },
                },
                {
                    id: 'customColorsSolidButtonText',
                    type: 'color',
                    label: 'Custom solid button text',
                    group: 'General',
                    default: { ref: 'custom', value: '#ffffff' },
                    visibleWhen: { field: 'colorScheme', equals: 'custom' },
                },
                {
                    id: 'customColorsOutlineButton',
                    type: 'color',
                    label: 'Custom outline button',
                    group: 'General',
                    default: { ref: 'custom', value: '#dd1d1d' },
                    visibleWhen: { field: 'colorScheme', equals: 'custom' },
                },
                {
                    id: 'transparentContainerColor',
                    type: 'select',
                    label: 'Transparent container color',
                    group: 'General',
                    default: 'white',
                    info: 'Text colour when the text box is hidden (text sits over the image).',
                    visibleWhen: { field: 'showTextBox', equals: false },
                    options: [
                        { value: 'white', label: 'White' },
                        { value: 'black', label: 'Black' },
                    ],
                },
                // ── Mobile (Shrine `__header_mobile`). ───────────────────────────────
                {
                    id: 'textAlignmentMobile',
                    type: 'select',
                    label: 'Text alignment (mobile)',
                    group: 'Mobile',
                    default: 'center',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
            ],
        },
    ],
    maxBlocks: 8,
};
export const slideshowPresets = [
    {
        name: 'Slideshow',
        // PREMIUM DEFAULT hero (founder direction 2026-07-03): the slideshow is the home's centrepiece, so its
        // component-level default ships a finished FR two-slide hero — real marketing copy over a lifestyle photo
        // with a CTA — NOT Shrine's empty English-placeholder canvas. This is the single component-level default
        // (same when the seller drops a slideshow anywhere), per the "default copy belongs to the component" rule.
        // Les diapos partent SANS image (direction fondatrice 2026-09-16) : chacune affiche le placeholder
        // scénique tant que la vendeuse n'a pas déposé sa photo. Section-level: full-bleed, no
        // vertical padding (height comes from `imageRatio`); the rest inherits the Shrine-matched field defaults.
        settings: {
            paddingTop: 0,
            paddingBottom: 0,
        },
        blocks: [
            {
                type: 'slide',
                settings: {
                    imageOverlayOpacity: 30,
                    heading: '@t:section.slide1_heading',
                    headingSize: 'h0',
                    subheading: '@t:section.slide1_subheading',
                    link: { type: 'collection', slug: 'all' },
                    showTextBox: false,
                    transparentContainerColor: 'white',
                    boxAlign: 'middle-left',
                    textAlignment: 'left',
                    textAlignmentMobile: 'center',
                },
            },
            {
                type: 'slide',
                settings: {
                    imageOverlayOpacity: 35,
                    heading: '@t:section.slide2_heading',
                    headingSize: 'h0',
                    subheading: '@t:section.slide2_subheading',
                    buttonLabel: '@t:section.view_shop',
                    link: { type: 'collection', slug: 'all' },
                    showTextBox: false,
                    transparentContainerColor: 'white',
                    boxAlign: 'middle-left',
                    textAlignment: 'left',
                    textAlignmentMobile: 'center',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const slideshowMeta = {
    label: 'Slideshow',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map